// Student view — full-screen, no brand. Mobile-first: tap = click,
// drag = scroll, soft keyboard pops up on tap (via hidden <input>),
// IME (composition) works on iOS/Android.
//
// Post share-token removal: no token, no /s/:slug. Anyone hitting cali8888.vip
// (or www.cali8888.vip) lands here and the WS connects to /api/student/ws.
// If the operator app isn't running, the DO closes the WS with a
// {type:'service-stopped'} envelope and we render "服務停止中".

import { useEffect, useRef, useState } from 'react';
import {
  newCallsClient,
  type CallsClient,
  type RemoteTrackEntry,
} from '../lib/calls';
import { captureInput, type InputCaptureHandle, type InputEvent } from '../lib/input';

type Phase = 'connecting' | 'awaiting-operator' | 'connected' | 'service-stopped' | 'error';

interface ConfigResponse {
  callsAppId: string | null;
  ts: number;
}

function collectDeviceInfo() {
  const w = window;
  return {
    userAgent: navigator.userAgent,
    width:  Math.round(w.innerWidth  || w.screen?.width  || 390),
    height: Math.round(w.innerHeight || w.screen?.height || 844),
    dpr: w.devicePixelRatio || 1,
    touch: 'ontouchstart' in w || (navigator.maxTouchPoints || 0) > 0,
    platform: (navigator as any).userAgentData?.platform || navigator.platform || '',
    mobile: !!(w.matchMedia && w.matchMedia('(pointer: coarse)').matches),
    locale: navigator.language || 'en-US',
    timezone: (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return 'UTC'; } })(),
  };
}

export default function Student() {
  const [phase, setPhase] = useState<Phase>('connecting');
  const [errMsg, setErrMsg] = useState<string>('');
  const [hideOverlay, setHideOverlay] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const keyboardInputRef = useRef<HTMLInputElement | null>(null);
  const callsRef = useRef<CallsClient | null>(null);
  const liveTracksRef = useRef<Map<number, MediaStream>>(new Map());
  const lastEntryRef = useRef<Map<number, RemoteTrackEntry>>(new Map());
  const liveTileIdRef = useRef<number | null>(null);
  // tileId -> dispose-fn that clears the stuck-frame watchdog interval.
  const stuckWatchdogsRef = useRef<Map<number, () => void>>(new Map());
  const inputHandleRef = useRef<InputCaptureHandle | null>(null);
  const blurTimerRef = useRef<number | null>(null);
  const phaseRef = useRef<Phase>('connecting');
  phaseRef.current = phase;

  const markConnected = () => {
    setPhase((p) => (p === 'connected' ? p : 'connected'));
    setTimeout(() => setHideOverlay(true), 600);
  };

  const sendInput = (ev: InputEvent) => {
    const ws = wsRef.current;
    if (!ws) { console.warn('[student] sendInput: no ws'); return; }
    if (ws.readyState !== WebSocket.OPEN) { console.warn('[student] sendInput: ws not open, state=' + ws.readyState); return; }
    if (liveTileIdRef.current == null) { console.warn('[student] sendInput: no tileId yet, ev=', ev.kind); }
    try {
      ws.send(JSON.stringify({ type: 'input', tileId: liveTileIdRef.current, event: ev }));
      if (ev.kind !== 'mouse' || ev.type !== 'move') {
        console.log('[student] sendInput', ev.kind, (ev as any).type || (ev as any).phase || '');
      }
    } catch (e) {
      console.warn('[student] sendInput err', e);
    }
  };

  useEffect(() => {
    let ws: WebSocket | null = null;
    let cancelled = false;
    let reconnectTimer: number | undefined;

    async function attachTrack(entry: RemoteTrackEntry) {
      const calls = callsRef.current;
      if (!calls) { console.warn('[student] attachTrack: callsRef.current null'); return; }
      const prevEntry = lastEntryRef.current.get(entry.tileId);
      const sameSession = prevEntry
        && prevEntry.callsSessionId === entry.callsSessionId
        && prevEntry.tracks.length === entry.tracks.length
        && prevEntry.tracks.every((t, i) => t.trackName === entry.tracks[i]?.trackName);
      if (liveTracksRef.current.has(entry.tileId) && sameSession) {
        console.log('[student] attachTrack: tileId=' + entry.tileId + ' identical entry, skip');
        return;
      }
      if (liveTracksRef.current.has(entry.tileId)) {
        console.log('[student] attachTrack: tileId=' + entry.tileId + ' republished, teardown old');
        const old = liveTracksRef.current.get(entry.tileId);
        if (old) old.getTracks().forEach((t) => t.stop());
        liveTracksRef.current.delete(entry.tileId);
      }
      lastEntryRef.current.set(entry.tileId, entry);
      console.log('[student] subscribe START', entry);
      try {
        const triggerResubscribe = (reason: string) => {
          if (cancelled) return;
          console.warn('[student] resubscribe tileId=' + entry.tileId + ' reason=' + reason);
          const old = liveTracksRef.current.get(entry.tileId);
          if (old) old.getTracks().forEach((t) => t.stop());
          liveTracksRef.current.delete(entry.tileId);
          // Clear any active stuck-watchdog timers so they don't double-fire.
          const wd = stuckWatchdogsRef.current.get(entry.tileId);
          if (wd) { wd(); stuckWatchdogsRef.current.delete(entry.tileId); }
          window.setTimeout(() => {
            if (cancelled) return;
            const latest = lastEntryRef.current.get(entry.tileId);
            if (latest) attachTrack(latest);
          }, 1500);
        };
        const stream = await calls.subscribe(entry, {
          onLost: () => triggerResubscribe('pc-lost'),
        });
        console.log('[student] subscribe OK tracks=' + stream.getTracks().map(t => t.kind).join(','));
        liveTracksRef.current.set(entry.tileId, stream);
        liveTileIdRef.current = entry.tileId;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch((e) => console.warn('video.play()', e));
          // Stuck-frame watchdog: if `currentTime` doesn't advance for 3.5 s
          // while we're supposedly playing, the WebRTC track is "alive" but
          // decoding has stalled (typical: missed keyframe never recovered
          // because PLI didn't make it back to operator). Force a fresh
          // subscribe to grab a new keyframe.
          const video = videoRef.current;
          let lastT = video.currentTime;
          let stuckSince = 0;
          const id = window.setInterval(() => {
            if (cancelled) return;
            const cur = video.currentTime;
            if (cur !== lastT) {
              lastT = cur;
              stuckSince = 0;
              return;
            }
            // currentTime didn't advance — see if it's because the video is
            // genuinely paused (we never resubscribe in that case).
            if (video.paused || video.readyState < 2) {
              stuckSince = 0;
              return;
            }
            if (stuckSince === 0) stuckSince = Date.now();
            else if (Date.now() - stuckSince > 3500) {
              stuckSince = 0;  // arm again after the resubscribe
              triggerResubscribe('frame-stuck');
            }
          }, 500);
          // Remember the disposer so detachTrack / resubscribe can stop it.
          const prev = stuckWatchdogsRef.current.get(entry.tileId);
          if (prev) prev();
          stuckWatchdogsRef.current.set(entry.tileId, () => window.clearInterval(id));
        }
        markConnected();
        wireInputCapture();
      } catch (e) {
        console.error('[student] subscribe FAILED', e);
      }
    }

    function detachTrack(tileId: number) {
      const stream = liveTracksRef.current.get(tileId);
      if (!stream) return;
      stream.getTracks().forEach((t) => t.stop());
      liveTracksRef.current.delete(tileId);
      lastEntryRef.current.delete(tileId);
      const wd = stuckWatchdogsRef.current.get(tileId);
      if (wd) { wd(); stuckWatchdogsRef.current.delete(tileId); }
      const remaining = liveTracksRef.current.entries().next().value as [number, MediaStream] | undefined;
      if (remaining) {
        liveTileIdRef.current = remaining[0];
        if (videoRef.current) videoRef.current.srcObject = remaining[1];
      } else {
        liveTileIdRef.current = null;
        if (videoRef.current) videoRef.current.srcObject = null;
        if (inputHandleRef.current) { inputHandleRef.current.destroy(); inputHandleRef.current = null; }
        setPhase('awaiting-operator');
        setHideOverlay(false);
      }
    }

    function broadcastDeviceUpdate() {
      const w = wsRef.current;
      if (!w || w.readyState !== WebSocket.OPEN) return;
      try {
        w.send(JSON.stringify({ type: 'device-update', device: collectDeviceInfo() }));
      } catch (_) {}
    }

    function wireInputCapture() {
      if (inputHandleRef.current) return;
      const input = keyboardInputRef.current;
      const video = videoRef.current;
      if (!input || !video) return;
      const unmute = () => {
        setTimeout(() => {
          try { video.muted = false; video.play().catch(() => {}); } catch {}
        }, 1000);
        input.removeEventListener('pointerdown', unmute);
      };
      input.addEventListener('pointerdown', unmute, { once: true });
      inputHandleRef.current = captureInput(input, video, sendInput, {
        keyboardTarget: input,
      });

      let resizeTimer: number | null = null;
      const onGeomChange = () => {
        if (resizeTimer) window.clearTimeout(resizeTimer);
        resizeTimer = window.setTimeout(() => { broadcastDeviceUpdate(); resizeTimer = null; }, 250);
      };
      window.addEventListener('orientationchange', onGeomChange);
      broadcastDeviceUpdate();

      const inner = inputHandleRef.current;
      inputHandleRef.current = {
        destroy() {
          if (resizeTimer) window.clearTimeout(resizeTimer);
          window.removeEventListener('orientationchange', onGeomChange);
          inner && inner.destroy();
        },
      };
    }

    async function fetchConfigOnce() {
      if (callsRef.current) return;
      const r = await fetch('/api/config');
      if (!r.ok) throw new Error('config fetch failed');
      const j = (await r.json()) as ConfigResponse;
      callsRef.current = newCallsClient({
        appId: j.callsAppId,
        apiBase: '/api/calls',
      });
    }

    async function connect() {
      try {
        await fetchConfigOnce();
        if (cancelled) return;

        const dev = collectDeviceInfo();
        const dParam = 'd=' + encodeURIComponent(JSON.stringify(dev));
        const wsScheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const wsUrl = `${wsScheme}//${window.location.host}/api/student/ws?${dParam}`;
        ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        ws.addEventListener('open', () => {
          console.log('[student-ws] open');
          // Stay in 'connecting' until we know whether operator is online.
          // The DO either drops us with service-stopped, or sends 'hello'
          // followed by track-snapshot.
        });
        ws.addEventListener('message', (ev) => {
          let msg: any;
          try { msg = JSON.parse(typeof ev.data === 'string' ? ev.data : ''); } catch (e) { console.warn('[student-ws] bad msg', e); return; }
          if (!msg || typeof msg !== 'object') return;
          console.log('[student-ws] msg', msg.type, msg);
          if (msg.type === 'service-stopped') {
            setPhase('service-stopped');
            setHideOverlay(false);
            return;
          }
          if (msg.type === 'hello') {
            setPhase('awaiting-operator');
            return;
          }
          if (msg.type === 'tracks-snapshot' && Array.isArray(msg.tracks)) {
            console.log('[student] tracks-snapshot count=' + msg.tracks.length);
            for (const t of msg.tracks as RemoteTrackEntry[]) attachTrack(t);
            return;
          }
          if (msg.type === 'track-registered' && msg.track) {
            console.log('[student] track-registered tileId=' + msg.track.tileId);
            attachTrack(msg.track as RemoteTrackEntry);
            return;
          }
          if (msg.type === 'track-unregistered' && typeof msg.tileId === 'number') { detachTrack(msg.tileId); return; }
          if (msg.type === 'from-operator' && msg.body && typeof msg.body === 'object') {
            const body = msg.body as { type?: string; editable?: boolean; hasEditable?: boolean };
            if (body.type === 'focus-changed') {
              console.log('[student] focus-changed editable=' + body.editable);
              return;
            }
            if (body.type === 'page-editable') {
              console.log('[student] page-editable=' + body.hasEditable);
              const k = keyboardInputRef.current;
              if (!k) return;
              k.disabled = !body.hasEditable;
              if (!body.hasEditable) {
                try { k.blur(); } catch (_) {}
              }
              return;
            }
          }
        });
        ws.addEventListener('close', (ev) => {
          const code = (ev as CloseEvent).code;
          console.warn('[student-ws] close', { code, reason: (ev as CloseEvent).reason });
          if (cancelled) return;
          // 4001 = operator offline. Don't auto-reconnect on a tight loop —
          // back off so the SPA actually shows the "service-stopped" screen.
          if (code === 4001 || phaseRef.current === 'service-stopped') {
            setPhase('service-stopped');
            setHideOverlay(false);
            reconnectTimer = window.setTimeout(connect, 5000);
            return;
          }
          if (phaseRef.current !== 'connected') {
            setPhase('connecting');
            setHideOverlay(false);
            reconnectTimer = window.setTimeout(connect, 1500);
          } else {
            reconnectTimer = window.setTimeout(connect, 1500);
          }
        });
        ws.addEventListener('error', (e) => {
          console.warn('[student-ws] error', e);
          if (!cancelled) setErrMsg('connection error');
        });
      } catch (e) { setPhase('error'); setErrMsg(String((e as Error).message || e)); }
    }

    connect();

    return () => {
      cancelled = true;
      if (ws) try { ws.close(); } catch {}
      if (reconnectTimer) window.clearTimeout(reconnectTimer);
      const calls = callsRef.current;
      if (calls) calls.destroy();
      callsRef.current = null;
      liveTracksRef.current.forEach((s) => s.getTracks().forEach((t) => t.stop()));
      liveTracksRef.current.clear();
      lastEntryRef.current.clear();
      stuckWatchdogsRef.current.forEach((dispose) => { try { dispose(); } catch (_) {} });
      stuckWatchdogsRef.current.clear();
      if (inputHandleRef.current) { inputHandleRef.current.destroy(); inputHandleRef.current = null; }
      if (blurTimerRef.current) { window.clearTimeout(blurTimerRef.current); blurTimerRef.current = null; }
    };
  }, []);

  let overlayText = '';
  if (phase === 'connecting') overlayText = '連線中…';
  else if (phase === 'awaiting-operator') overlayText = '連線中…';
  else if (phase === 'service-stopped') overlayText = '服務停止中';
  else if (phase === 'error') overlayText = errMsg ? `連線失敗: ${errMsg}` : '連線失敗';

  const overlayVisible = !hideOverlay && phase !== 'connected';
  const fatal = phase === 'service-stopped' || phase === 'error';

  return (
    <div className="student-root">
      <video ref={videoRef} className="student-video" autoPlay playsInline muted />
      <input
        ref={keyboardInputRef}
        className="student-keyboard"
        type="text"
        autoComplete="off"
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
        inputMode="text"
        disabled
        aria-label="remote control surface"
      />
      {overlayVisible && (
        <div className={`student-overlay ${fatal ? 'fatal' : ''}`}>
          <div className="overlay-card">
            <img className="overlay-logo" src="/cali-logo.png" alt="卡利集團" />
            <div className="msg">{overlayText}</div>
          </div>
        </div>
      )}
    </div>
  );
}
